import { IndianRupee } from 'lucide-react';
import { instituteToday } from '@/features/teacher/utils';
import { useApiQuery } from '@/hooks/useApiQuery';
import { formatCurrency, formatDate, formatMonth } from '@/lib/format';
import type { ApiQueryState } from '@/hooks/useApiQuery';
import type { StaffSalaryRecord } from '@/types/payroll';
import { payrollApi } from '../api/payroll.api';
import { SALARY_REVISION_LABEL } from '../constants';
import { describeIncrement, lastIncrementOf } from '../format';

export interface SalaryOverviewCardProps {
  staffUuid: string;
  /** The profile's salary read. Null data with no error: not loaded yet. */
  salary: ApiQueryState<StaffSalaryRecord | null>;
  /** Whether the viewer holds `PAYROLL_VIEW`. Without it nothing is fetched or shown. */
  canView: boolean;
  /** The institute's own administrator is not on payroll. */
  isOwner: boolean;
}

/** One line of the breakdown. */
function Row({ label, amount }: { label: string; amount: string }) {
  return (
    <div className="flex justify-between gap-3 text-slate-600 dark:text-slate-400">
      <span className="truncate">{label}</span>
      <span className="font-bold tabular-nums text-slate-900 dark:text-slate-100">{formatCurrency(amount)}</span>
    </div>
  );
}

/**
 * The salary in force today, on the profile's Overview.
 *
 * Drawn in the same card as the rest of the Overview so the page reads as one.
 * Every figure is the server's — the salary set on the Payroll tab — and the card
 * says plainly when there is none, rather than showing a sample.
 */
export function SalaryOverviewCard({ staffUuid, salary, canView, isOwner }: SalaryOverviewCardProps) {
  const current = salary.data?.current ?? null;
  const upcoming = salary.data?.upcoming ?? null;

  let body: React.ReactNode;
  if (!canView) {
    body = <p className="text-sm text-slate-500 dark:text-slate-400">Salaries are visible only to those who manage payroll.</p>;
  } else if (isOwner) {
    body = <p className="text-sm text-slate-500 dark:text-slate-400">The administrator account is not paid through payroll.</p>;
  } else if (salary.isLoading) {
    body = <p className="text-sm text-slate-400">Loading the salary…</p>;
  } else if (salary.error) {
    body = <p className="text-sm text-rose-600 dark:text-rose-400">Could not load the salary. Open the Payroll tab to try again.</p>;
  } else if (!current) {
    body = (
      <p className="text-sm text-slate-500 dark:text-slate-400">
        {upcoming
          ? `A salary starts on ${formatDate(upcoming.effectiveFrom)}. Nothing is in force before then.`
          : 'No salary has been set yet. Set one from the Payroll tab.'}
      </p>
    );
  } else {
    const earnings = current.lines.filter((line) => line.kind === 'EARNING');
    const deductions = current.lines.filter((line) => line.kind === 'DEDUCTION');
    body = (
      <>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 py-2 bg-slate-50 dark:bg-slate-800/50 rounded-xl px-3 text-center border border-slate-100 dark:border-slate-800">
          <div>
            <span className="text-xs font-semibold text-slate-400 block uppercase">Basic Salary</span>
            <span className="text-sm font-black text-slate-900 dark:text-slate-100">{formatCurrency(current.basic)}</span>
          </div>
          <div>
            <span className="text-xs font-semibold text-slate-400 block uppercase">Allowances</span>
            <span className="text-sm font-black text-slate-900 dark:text-slate-100">{formatCurrency(current.allowances)}</span>
          </div>
          <div>
            <span className="text-xs font-semibold text-slate-400 block uppercase">Deductions</span>
            <span className="text-sm font-black text-slate-900 dark:text-slate-100">{formatCurrency(current.totalDeductions)}</span>
          </div>
          <div>
            <span className="text-xs font-extrabold text-emerald-600 dark:text-emerald-400 block uppercase">Net Pay</span>
            <span className="text-base font-black text-emerald-600 dark:text-emerald-400">{formatCurrency(current.netPay)}</span>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4 text-xs sm:text-sm pt-0.5">
          <div className="space-y-1.5">
            <div className="text-xs font-bold text-emerald-600 dark:text-emerald-400 uppercase tracking-wider">Earnings</div>
            <Row label="Basic Salary" amount={current.basic} />
            {earnings.map((line) => (
              <Row key={line.componentUuid} label={line.name} amount={line.amount} />
            ))}
            <div className="flex justify-between font-extrabold text-emerald-600 dark:text-emerald-400 pt-1 border-t border-slate-100 dark:border-slate-800">
              <span>Total Earnings</span>
              <span className="tabular-nums">{formatCurrency(current.grossEarnings)}</span>
            </div>
          </div>

          <div className="space-y-1.5 border-l border-slate-100 dark:border-slate-800 pl-3">
            <div className="text-xs font-bold text-rose-500 uppercase tracking-wider">Deductions</div>
            {deductions.length === 0 ? (
              <p className="text-slate-400">None</p>
            ) : (
              deductions.map((line) => <Row key={line.componentUuid} label={line.name} amount={line.amount} />)
            )}
            <div className="flex justify-between font-extrabold text-rose-600 dark:text-rose-400 pt-1 border-t border-slate-100 dark:border-slate-800">
              <span>Total Deductions</span>
              <span className="tabular-nums">{formatCurrency(current.totalDeductions)}</span>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <div className="bg-white dark:bg-slate-900 border border-slate-200/90 dark:border-slate-800 rounded-2xl p-4 shadow-xs space-y-3">
      <div className="flex items-center justify-between border-b border-slate-100 dark:border-slate-800/80 pb-2.5">
        <div className="flex items-center gap-2.5 text-base font-extrabold text-slate-900 dark:text-white">
          <IndianRupee className="w-4.5 h-4.5 text-emerald-600 dark:text-emerald-400" aria-hidden />
          <span>
            Salary{' '}
            {current && (
              <span className="text-xs font-normal text-slate-400">(from {formatDate(current.effectiveFrom)})</span>
            )}
          </span>
        </div>
      </div>
      {body}
      {canView && !isOwner && !salary.isLoading && !salary.error && (
        <>
          <LastIncrementLine salary={salary.data} />
          <RecentPayments staffUuid={staffUuid} />
        </>
      )}
    </div>
  );
}

/** The newest increment or promotion in force — the Payroll tab's own rule. */
function LastIncrementLine({ salary }: { salary: StaffSalaryRecord | null }) {
  const last = salary ? lastIncrementOf(salary.history, instituteToday()) : null;

  return (
    <p className="text-sm text-slate-600 dark:text-slate-400 border-t border-slate-100 dark:border-slate-800/80 pt-2.5">
      {last ? (
        <>
          Last increment:{' '}
          <strong className="text-emerald-600 dark:text-emerald-400">
            {describeIncrement(last.incrementPercent) ?? SALARY_REVISION_LABEL[last.revisionType]}
          </strong>{' '}
          from {formatDate(last.effectiveFrom)}
        </>
      ) : (
        'No increment yet'
      )}
    </p>
  );
}

/**
 * The last five months as paid, read-only — recording a payment stays on the
 * Payroll tab. The same read the tab makes, so a month shows the same there.
 */
function RecentPayments({ staffUuid }: { staffUuid: string }) {
  const { data, isLoading, error } = useApiQuery(() => payrollApi.listSalaryPayments(staffUuid), [staffUuid]);
  const months = data?.months ?? [];

  return (
    <div className="space-y-1.5">
      <div className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
        Paid — last 5 months
      </div>

      {isLoading && <p className="text-sm text-slate-400">Loading payments…</p>}
      {error && (
        <p className="text-sm text-rose-600 dark:text-rose-400">
          Could not load the payments. Open the Payroll tab to try again.
        </p>
      )}
      {!isLoading && !error && months.length === 0 && (
        <p className="text-sm text-slate-500 dark:text-slate-400">No salary months to show yet.</p>
      )}

      {months.length > 0 && (
        <table className="w-full text-xs sm:text-sm">
          <caption className="sr-only">Salary paid in the last five months, newest first</caption>
          <thead>
            <tr className="text-left text-xs font-semibold text-slate-400">
              <th scope="col" className="py-1 pr-2 font-semibold">Month</th>
              <th scope="col" className="py-1 pr-2 text-right font-semibold">Paid</th>
              <th scope="col" className="py-1 font-semibold">Paid on</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
            {months.map((row) => (
              <tr key={row.month}>
                <td className="py-1.5 pr-2 text-slate-700 dark:text-slate-300">{formatMonth(`${row.month}-01`)}</td>
                <td className="py-1.5 pr-2 text-right font-bold tabular-nums text-slate-900 dark:text-slate-100">
                  {row.payment ? formatCurrency(row.payment.amount) : '—'}
                </td>
                <td className="py-1.5">
                  {row.payment ? (
                    <span className="text-slate-700 dark:text-slate-300">{formatDate(row.payment.paidOn)}</span>
                  ) : row.due ? (
                    // Due but nothing recorded: the month the office has to chase.
                    <span className="font-semibold text-rose-600 dark:text-rose-400">Not paid</span>
                  ) : (
                    <span className="text-slate-400">No salary due</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
